/**
 * Shared "load once, reload only when invalidated" cache control (PX-W6.1,
 * a port of xelerate-ai's `store/fetchCache.ts`; key-scoped loads and
 * mid-flight server changes added by TASK-3D6DB8B6).
 *
 * The model, applied per resource slice:
 *  - `invalidated` starts `true`, so the first access loads exactly once.
 *  - A successful load flips it `false` and records the `loadedKey` (the
 *    argument the data was loaded for — e.g. a projectId or orgId).
 *  - The load thunk's `condition` (via {@link shouldFetch}) then skips every
 *    redundant dispatch: concurrent ones (several components mounting the same
 *    route, React StrictMode's double-invoked effect) AND sequential ones
 *    (navigating away and back) — until the cache is invalidated again.
 *  - Switching context (a different projectId / orgId) changes the key, which
 *    `shouldFetch` treats as needing a fresh load. That is how selecting a
 *    different project automatically reloads its data — no manual
 *    invalidation event required.
 *  - A mutation the slice does not apply optimistically calls {@link invalidate}
 *    so the next access reloads the server truth.
 *
 * Key-scoped loads (opt-in). A slice that passes its key to
 * {@link fetchStarted} gets a PER-KEY in-flight veto: a load for project B is
 * no longer vetoed by an in-flight load for project A, while a same-key
 * re-dispatch still is (StrictMode dedupe preserved). The settles then return
 * `false` for a SUPERSEDED key — A's late reply or failure after B started —
 * and the caller writes nothing. Calling `fetchStarted(cache)` without a key
 * keeps the legacy global veto, bit for bit. With keys A→B→A in flight, both A
 * settles are applied (each matches the current key): harmless, the second
 * one lands the newer rows.
 *
 * {@link invalidate} vs {@link markServerChanged}. Both mark the cache stale.
 * `invalidate` is cleared by the settle of a load that is in flight — right
 * for close reducers and Retry-only re-arms, which StrictMode runs BETWEEN a
 * panel's two effect runs and which must never schedule a reload.
 * `markServerChanged` is for a SERVER change (a realtime frame, a post-mutation
 * re-arm): arriving mid-flight it survives the success, so the cache stays
 * stale and the follow-up listener (`store/cacheFollowUps.ts`) issues exactly
 * one more load. A failed load drops the change (a failure never auto-retries).
 *
 * {@link fetchFailed} still never records `loadedKey`, so a remount after a
 * first-load failure tries again (C-5, unchanged). And a keyed load for a
 * NEW key clears the old `loadedKey` as it starts, so after "A loaded, B
 * refused" the cache no longer claims A is fresh (fix cycle 3).
 *
 * Why a thunk `condition` and not an effect-level `if (!loading)` guard:
 * `condition` reads the LIVE store at dispatch time, after the first dispatch's
 * `pending` has synchronously committed `loading: true`. An effect guard reads
 * the component's captured render snapshot, which is still `loading: false`
 * during StrictMode's double-invoke and during two components mounting in the
 * same commit — so it cannot dedupe those. The condition can.
 */

/** The fields a cacheable slice embeds in its state. */
export interface FetchCache {
  /** True while a load is in flight. */
  loading: boolean
  /** True when the next access must reload (initial load, or after a mutation). */
  invalidated: boolean
  /** The arg the cached data was loaded for; a change forces a reload. */
  loadedKey: string | null
  /** Key of the most recently STARTED load (meaningful only when `keyScoped`). */
  loadingKey: string | null
  /** The latest load was started with an explicit key: per-key veto, superseded settles dropped. */
  keyScoped: boolean
  /** {@link markServerChanged} ran while the latest load was in flight. */
  changedWhileLoading: boolean
}

/** Defaults for a cacheable slice's `initialState` (invalidated → load once). */
export const initialFetchCache: FetchCache = {
  loading: false,
  invalidated: true,
  loadedKey: null,
  loadingKey: null,
  keyScoped: false,
  changedWhileLoading: false
}

/**
 * Thunk `condition` predicate. Returns `false` (skip the dispatch entirely)
 * when a load for this key is already in flight (any key, for a legacy
 * unkeyed load), or the cache is already fresh for `key`.
 *
 * @param cache - The slice's current cache fields (read from `getState()`).
 * @param key   - The arg identifying this load (projectId / orgId / null).
 */
export function shouldFetch(cache: FetchCache, key: string | null = null): boolean {
  if (cache.loading) return cache.keyScoped && cache.loadingKey !== key
  return cache.invalidated || cache.loadedKey !== key
}

/**
 * Mark a load as started. Call from the thunk's `pending` reducer. Passing
 * `key` opts into the per-key veto and superseded-settle drop; omitting it
 * keeps the legacy global veto.
 *
 * A keyed start for a key other than `loadedKey` also drops that claim
 * (fix cycle 3): the slice's `pending` reset is about to replace (or has
 * wiped) the rows loaded for the old key, so a later open of the old key —
 * say, after this load is refused — must load again rather than be vetoed as
 * fresh. A same-key restart and a legacy unkeyed start keep `loadedKey`.
 */
export function fetchStarted(cache: FetchCache, key?: string | null): void {
  if (key !== undefined && key !== cache.loadedKey) cache.loadedKey = null
  cache.loading = true
  cache.changedWhileLoading = false
  cache.keyScoped = key !== undefined
  cache.loadingKey = key ?? null
}

/**
 * Mark a load as succeeded for `key`. Call from the `fulfilled` reducer, as
 * its FIRST statement: `false` means the reply is for a superseded key and the
 * caller must write nothing. On success, records the key and clears
 * `invalidated` — unless the server changed mid-flight, which keeps it stale.
 */
export function fetchSucceeded(cache: FetchCache, key: string | null = null): boolean {
  if (cache.keyScoped && key !== cache.loadingKey) return false
  cache.loading = false
  cache.loadedKey = key
  cache.invalidated = cache.changedWhileLoading
  cache.changedWhileLoading = false
  return true
}

/**
 * Mark a load as failed. Call from the `rejected` reducer, as its FIRST
 * statement: `false` means the failure is for a superseded key and the caller
 * must write nothing. Leaves `invalidated` false so a failed load does NOT
 * auto-retry (the error UI re-arms it via {@link invalidate}), drops any
 * mid-flight server change, and records no `loadedKey` — so a context/key
 * change or a remount still forces a fresh attempt.
 */
export function fetchFailed(cache: FetchCache, key?: string | null): boolean {
  if (cache.keyScoped && key !== undefined && key !== cache.loadingKey) return false
  cache.loading = false
  cache.invalidated = false
  cache.changedWhileLoading = false
  return true
}

/**
 * Force the next access to reload. Call from close reducers, Retry re-arms and
 * non-optimistic mutations; a load already in flight clears it on settle.
 */
export function invalidate(cache: FetchCache): void {
  cache.invalidated = true
}

/**
 * The SERVER's truth changed (realtime frame, post-mutation re-arm). Like
 * {@link invalidate}, and additionally survives the success of a load in
 * flight so exactly one follow-up load picks the change up.
 */
export function markServerChanged(cache: FetchCache): void {
  cache.invalidated = true
  if (cache.loading) cache.changedWhileLoading = true
}
